/** @jsxImportSource solid-js */

import { createSignal, Show } from "solid-js";
import { globalState } from "#frontend/state";
import { Button } from "#palette/components/button.solid";

export async function shouldShowAmpersandsPage(): Promise<boolean> {
  const ampersandsActivated =
    (await globalState.client.getFlag("UI_ENV_USE_ADVOCATE_COPY")) === "1";
  return (
    ampersandsActivated &&
    !(globalState.client.userData.acceptedAmpersands ?? false)
  );
}

function AmpersandsConsentConfirmation(props: { goNext: () => void }) {
  return (
    <>
      <h1 class="text-lg font-bold">
        Thank you! Your preferences have been updated successfully.
      </h1>
      <p>
        As part of the process of entering into matching, you previously
        consented to being contacted by an attorney. In light of our transition
        to partnering with Ampersands Advocacy Resources to use advocates, we
        asked you to update your consent. If a match occurs, an advocate through
        Ampersands Advocacy Services will reach out to you via the preferred
        contact method(s) you provided when creating your account to help you
        navigate your options at your own pace and on your own terms.
      </p>
      <p class="my-4">
        <strong>Note:</strong> A matched survivor is a survivor who has entered
        into matching and whose unique identifier matches the unique identifier
        entered by another survivor, indicating that both survivors have
        identified the same perpetrator.
      </p>
      <p class="mb-6">
        For more information about our partnership with Ampersands Advocacy
        Resources,{" "}
        <a
          href="https://www.ampersandsar.com/"
          target="_blank"
          rel="noopener noreferrer"
          class="underline"
        >
          click here
        </a>
        .
      </p>
      <Button onClick={props.goNext}>Continue</Button>
    </>
  );
}

export function AmpersandsConsentPage(props: { goNext: () => void }) {
  const [accepted, setAccepted] = createSignal(false);

  return (
    <Show
      when={!accepted()}
      fallback={<AmpersandsConsentConfirmation goNext={props.goNext} />}
    >
      <h1 class="text-lg font-bold">Please update your consent settings</h1>
      <p>
        When you entered into matching, you consented to being contacted by a
        pro bono attorney. Because we have transitioned to a partnership with
        Ampersands Advocacy Services to utilize advocates instead of attorneys,
        we ask that you update your consent. Should a match occur, an advocate
        will reach out to you using the preferred contact method(s) you provided
        when creating your entry to help you understand your options at your own
        pace and on your own terms. Advocates are not attorneys. Your
        communications with them will be confidential, but they are not covered
        by attorney-client privilege and could be disclosed through legal
        process such as a subpoena or court order.
      </p>
      <div class="my-5">
        <p class="font-bold">Please indicate your preference below:</p>
        <div class="flex gap-8">
          <Button
            class="bg-status-success-bg text-status-success-fg hover:bg-status-success-fg hover:text-status-success-bg"
            onClick={async () => {
              await globalState.client.updateConsent("ampersandsApr2026", true);
              globalState.client.userData.acceptedAmpersands = true;
              await globalState.client.saveUserData();

              setAccepted(true);
            }}
          >
            Yes, I consent to being contacted by a confidential advocate in the
            event of a match.
          </Button>
          <Button
            class="bg-status-error-bg text-status-error-fg hover:bg-status-error-fg hover:text-status-error-bg"
            onClick={async () => {
              await globalState.client.updateConsent(
                "ampersandsApr2026",
                false,
              );
              globalState.client.userData.acceptedAmpersands = true;
              await globalState.client.saveUserData();

              props.goNext();
            }}
          >
            I do not consent to being contacted by a confidential advocate in
            the event of a match.
          </Button>
        </div>

        <p class="font-bold">
          Please note that without your consent, we cannot guarantee that you
          will be reached should a match occur.
        </p>
      </div>
      <p>
        <strong>Note:</strong> With this consent, if and/or when you match with
        another survivor, a free, confidential advocate from Ampersands Advocacy
        Resources will reach out to help you understand your options, whether
        that means legal action, Title IX processes, restorative justice, or
        connecting you to community-based resources like counseling, medical
        care, or campus support. Confidential advocates are there to inform and
        support, but every decision remains entirely yours.
      </p>
    </Show>
  );
}
